import React, { useEffect, useState } from "react";
import { Wallet, ChevronRight, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { ModalPortal } from "./ModalPortal";

const KIND_LABELS = {
    goodwill: "Goodwill",
    promo: "Promo",
    refund: "Refund",
    adjustment: "Adjustment",
    redeemed: "Redeemed",
    expired: "Expired",
    removed: "Removed",
};

export const StoreCreditCard = ({ balance = 0 }) => {
    const [open, setOpen] = useState(false);
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || history) return;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/me/store-credit/history");
                setHistory(data.entries || []);
            } catch {
                toast.error("Could not load credit history");
            }
            setLoading(false);
        })();
    }, [open, history]);

    return (
        <>
            <div className="glass-elevated rounded-2xl p-6 relative overflow-hidden" data-testid="store-credit-card"
                 style={{ background: "radial-gradient(circle at bottom left, rgba(52,211,153,0.1), transparent 60%)" }}>
                <div className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full opacity-20"
                     style={{ background: "radial-gradient(circle, rgba(52,211,153,0.6), transparent 70%)" }} />
                <div className="relative flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 flex items-center gap-1.5">
                            <Wallet size={12} /> Store credit
                        </p>
                        <p className="font-display text-3xl font-extrabold text-white italic tracking-tighter">
                            <span className="text-emerald-300">A$</span> {Number(balance).toFixed(2)}
                        </p>
                        <p className="text-xs text-[var(--text-secondary)] mt-1">
                            Apply automatically at checkout. Never expires.
                        </p>
                    </div>
                    <button
                        onClick={() => setOpen(true)}
                        className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:text-white inline-flex items-center gap-1"
                        data-testid="open-credit-history"
                    >
                        History <ChevronRight size={14} />
                    </button>
                </div>
            </div>

            {open && (
                <ModalPortal>
                <div
                    className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
                    onClick={() => setOpen(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="glass-elevated w-full sm:max-w-lg max-h-[80vh] overflow-hidden rounded-t-2xl sm:rounded-2xl flex flex-col"
                        data-testid="credit-history-modal"
                    >
                        <div className="flex items-center justify-between p-5 border-b border-white/5">
                            <div>
                                <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Store credit ledger</p>
                                <p className="font-display text-xl font-bold text-white">A$ {Number(balance).toFixed(2)} available</p>
                            </div>
                            <button onClick={() => setOpen(false)} className="text-[var(--text-secondary)] hover:text-white" data-testid="close-credit-history">
                                <X size={18} />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4">
                            {loading && <div className="text-center text-[var(--text-secondary)] py-8 text-sm">Loading…</div>}
                            {!loading && history && history.length === 0 && (
                                <div className="text-center text-[var(--text-secondary)] py-12 text-sm">
                                    No credit activity yet.
                                </div>
                            )}
                            <div className="space-y-2">
                                {(history || []).map((e) => {
                                    const isCredit = e.direction === "credit";
                                    return (
                                        <div
                                            key={e.id}
                                            className="flex items-center justify-between gap-3 p-3 rounded-xl bg-black/40 border border-white/5"
                                            data-testid={`credit-entry-${e.id}`}
                                        >
                                            <div className="min-w-0 flex-1">
                                                <p className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                                    {KIND_LABELS[e.kind] || e.kind} · {new Date(e.created_at).toLocaleDateString("en-AU")}
                                                </p>
                                                <p className="text-sm text-white mt-0.5 line-clamp-2">{e.reason}</p>
                                                {e.order_ref && (
                                                    <p className="text-[10px] font-mono text-[var(--gold)] mt-0.5">{e.order_ref}</p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className={`font-mono font-bold ${isCredit ? "text-emerald-300" : "text-red-300"}`}>
                                                    {isCredit ? "+" : "-"}A$ {Number(e.amount).toFixed(2)}
                                                </p>
                                                <p className="text-[10px] text-[var(--text-muted)] mt-0.5">Bal A$ {Number(e.balance_after).toFixed(2)}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
                </ModalPortal>
            )}
        </>
    );
};
